import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Download, FileUp, MapPin, Briefcase, DollarSign, CheckCircle2, Mail, Smile, IdCard, ShieldCheck, Wifi, Award } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { API } from "../lib/api";

const CAND_IMG = "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1400&q=80";

const REMOTE_STATES = [
  { code: "MO", name: "Missouri", range: "$40,000 – $55,000" },
  { code: "AZ", name: "Arizona", range: "$40,000 – $55,000" },
  { code: "MT", name: "Montana", range: "$40,000 – $55,000" },
  { code: "AR", name: "Arkansas", range: "$40,000 – $55,000" },
  { code: "OK", name: "Oklahoma", range: "$40,000 – $55,000" },
  { code: "KY", name: "Kentucky", range: "$40,000 – $55,000" },
];

export default function ForCandidates() {
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    office_background: "",
    software_familiarity: "",
    desired_role: "",
    preferred_state: "Open to any",
    note: "",
  });
  const [cv, setCv] = useState(null);
  const [idDoc, setIdDoc] = useState(null);
  const [remoteOk, setRemoteOk] = useState(true);
  const [bgConsent, setBgConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [utm, setUtm] = useState({ utm_source: "", utm_medium: "", utm_campaign: "", utm_content: "", referrer: "" });

  const [guideName, setGuideName] = useState("");
  const [guideEmail, setGuideEmail] = useState("");
  const [guideLoading, setGuideLoading] = useState(false);

  useEffect(() => {
    // Capture UTM params from URL on first load and persist across the session
    const params = new URLSearchParams(window.location.search);
    const stored = JSON.parse(sessionStorage.getItem("dht_utm") || "null");
    const fresh = {
      utm_source: params.get("utm_source") || stored?.utm_source || "",
      utm_medium: params.get("utm_medium") || stored?.utm_medium || "",
      utm_campaign: params.get("utm_campaign") || stored?.utm_campaign || "",
      utm_content: params.get("utm_content") || stored?.utm_content || "",
      referrer: stored?.referrer || document.referrer || "",
    };
    setUtm(fresh);
    sessionStorage.setItem("dht_utm", JSON.stringify(fresh));

    axios.get(`${API}/jobs`).then((r) => setJobs(r.data)).catch(() => toast.error("Unable to load roles")).finally(() => setLoadingJobs(false));
  }, []);

  const handleCv = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("CV must be 8MB or smaller."); return; }
    setCv(f);
  };

  const handleId = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("ID file must be 8MB or smaller."); return; }
    setIdDoc(f);
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !cv) {
      toast.error("Name, email, and CV are required.");
      return;
    }
    if (!bgConsent) {
      toast.error("Please consent to the background check to continue.");
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
    fd.append("remote_ok", remoteOk ? "true" : "false");
    fd.append("bg_check_consent", "true");
    Object.entries(utm).forEach(([k, v]) => v && fd.append(k, v));
    fd.append("cv", cv);
    if (idDoc) fd.append("id_document", idDoc);
    try {
      await axios.post(`${API}/candidates`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Thanks! We've got your application. We'll be in touch within 2 business days.");
      setForm({ full_name: "", email: "", phone: "", office_background: "", software_familiarity: "", desired_role: "", preferred_state: "Open to any", note: "" });
      setCv(null); setIdDoc(null); setBgConsent(false);
      const cvi = document.getElementById("cv-input"); if (cvi) cvi.value = "";
      const idi = document.getElementById("id-input"); if (idi) idi.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGuide = async (e) => {
    e.preventDefault();
    if (!guideEmail || !guideName) { toast.error("Name and email required."); return; }
    setGuideLoading(true);
    try {
      await axios.post(`${API}/leads`, { full_name: guideName, email: guideEmail, magnet: "candidate_guide" });
      const link = document.createElement("a");
      link.href = `${API}/downloads/candidate-guide`;
      link.setAttribute("download", "Payroll-Interview-Prep-Guide.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Guide sent. Check your downloads.");
      setGuideEmail(""); setGuideName("");
    } catch {
      toast.error("Unable to send guide.");
    } finally {
      setGuideLoading(false);
    }
  };

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative pt-36 pb-20 bg-navy text-white overflow-hidden" data-testid="candidates-hero">
        <div className="absolute inset-0 hero-grid opacity-25" />
        <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#D4AF37]/10 blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="eyebrow text-[#D4AF37]">For Candidates</span>
            </div>
            <h1 className="mt-6 font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight">
              Your career in <em className="text-[#D4AF37] not-italic">payroll</em><br /> starts here.
            </h1>
            <p className="mt-6 text-lg text-white/80 max-w-xl leading-relaxed">
              New to payroll? You're in the right place. If you've worked in office admin, handled numbers,
              or just love a well-kept spreadsheet, we'll help you find a role with training and a team
              that's rooting for you.
            </p>
            <Link to="/career-pathway" className="mt-6 inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/15 backdrop-blur-sm px-5 py-2.5 hover:bg-[#D4AF37]/25 transition-all group" data-testid="cand-hero-prc-badge">
              <span className="flex items-center gap-2 rounded-full bg-[#D4AF37] px-3 py-0.5 text-[10px] font-bold tracking-widest text-[#0A2540]">
                <Award className="h-3 w-3" /> PRC CERTIFIED
              </span>
              <span className="text-sm text-white font-medium">Earn your PRC in 7-14 days · we sponsor your full career path</span>
              <ArrowRight className="h-4 w-4 text-[#D4AF37] group-hover:translate-x-1 transition-transform" />
            </Link>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a href="#apply" className="btn-gold" data-testid="candidates-hero-apply-btn">
                Submit Your CV <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#roles" className="btn-ghost !border-white/20 !text-white hover:!bg-white hover:!text-navy" data-testid="candidates-hero-roles-btn">
                Browse Open Roles
              </a>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-2xl">
              <img src={CAND_IMG} alt="Colleagues collaborating" className="w-full h-[440px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* REMOTE STATES BAND */}
      <section className="bg-[#F6FDF9] border-y border-[#10B981]/15 py-16" data-testid="remote-states-band">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="eyebrow text-[#10B981] flex items-center gap-2"><Wifi className="h-3.5 w-3.5" /> Remote Mid-Level Openings</div>
              <h2 className="mt-3 font-serif text-3xl md:text-4xl text-navy leading-tight">
                Now hiring remote payroll assistants in 6 states.
              </h2>
              <p className="mt-3 text-navy/60 max-w-2xl">
                Competitive annual compensation between <span className="font-semibold text-navy">$40,000 – $55,000</span> based on experience.
                Background check and government-issued ID verification required at offer stage.
              </p>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {REMOTE_STATES.map((s) => (
              <div key={s.code} className="rounded-xl border border-[#10B981]/20 bg-white p-5 hover:shadow-lg transition-all" data-testid={`state-${s.code}`}>
                <div className="flex items-center justify-between">
                  <div className="font-serif text-3xl text-navy leading-none">{s.code}</div>
                  <Wifi className="h-4 w-4 text-[#10B981]" />
                </div>
                <div className="mt-3 text-sm font-medium text-navy">{s.name}</div>
                <div className="mt-4 text-[11px] uppercase tracking-widest text-navy/45">Annual</div>
                <div className="font-mono text-sm text-[#0A2540] font-semibold">{s.range}</div>
                <div className="mt-2 text-[10px] uppercase tracking-widest text-navy/40">Remote · Mid-level</div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-2 text-xs text-navy/60">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-navy/5 px-3 py-1.5"><Wifi className="h-3 w-3 text-[#10B981]" /> Fully remote</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-navy/5 px-3 py-1.5"><ShieldCheck className="h-3 w-3 text-[#10B981]" /> Background check included</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-navy/5 px-3 py-1.5"><IdCard className="h-3 w-3 text-[#10B981]" /> Government-issued ID required</span>
          </div>
        </div>
      </section>

      {/* ROLES LIST */}
      <section className="py-24 bg-ivory" id="roles" data-testid="roles-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="eyebrow text-[#10B981]">Current Openings</div>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl text-navy leading-tight">
                Entry-to-mid level roles, live now.
              </h2>
              <p className="mt-3 text-navy/60 max-w-2xl">Every role below offers training — apply even if you're still learning the software.</p>
            </div>
            <div className="font-mono text-xs tracking-[0.3em] text-navy/50">
              {loadingJobs ? "LOADING…" : `${jobs.length} OPEN ROLES`}
            </div>
          </div>

          <div className="mt-12 space-y-4">
            {loadingJobs ? (
              <div className="card-lux">Loading roles…</div>
            ) : jobs.map((j) => (
              <div key={j.id} className="card-lux group cursor-default" data-testid={`job-${j.id}`}>
                <div className="grid md:grid-cols-12 gap-6 items-start">
                  <div className="md:col-span-5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[10px] tracking-[0.3em] text-navy/40">{j.type.toUpperCase()} · POSTED {j.posted_days_ago}D AGO</span>
                      {j.remote && <span className="inline-flex items-center gap-1 rounded-full bg-[#10B981]/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-[#10B981] font-semibold"><Wifi className="h-2.5 w-2.5" /> Remote</span>}
                    </div>
                    <h3 className="mt-3 font-serif text-2xl md:text-3xl text-navy">{j.title}</h3>
                    <p className="mt-2 text-sm text-navy/60 leading-relaxed">{j.description}</p>
                    {j.min_wage_base && (
                      <div className="mt-3 font-mono text-[10px] text-navy/45 tracking-wider uppercase">Benchmark · {j.min_wage_base}</div>
                    )}
                  </div>
                  <div className="md:col-span-4 space-y-2 text-sm text-navy/70">
                    <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#10B981]" /> {j.location}</div>
                    <div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-[#10B981]" /> {j.experience}</div>
                    <div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-[#10B981]" /> {j.salary_range}</div>
                    {j.background_check && (
                      <div className="flex items-center gap-2 text-navy/55"><ShieldCheck className="h-4 w-4 text-[#10B981]" /> Background check + ID verification</div>
                    )}
                  </div>
                  <div className="md:col-span-3 space-y-3">
                    <div className="flex flex-wrap gap-1.5">
                      {j.software.map((s) => (
                        <span key={s} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-navy/5 text-navy/60">{s}</span>
                      ))}
                    </div>
                    <a href="#apply" className="btn-gold w-full !py-2.5 text-sm" data-testid={`apply-${j.id}`}>
                      Apply <ArrowRight className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section className="py-28 bg-white" id="apply" data-testid="cv-upload-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <div className="eyebrow text-[#D4AF37]">Submit Your CV</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-navy leading-[1.05]">
              A short, friendly application.
            </h2>
            <p className="mt-6 text-navy/65 text-lg leading-relaxed">
              It's okay if you're still learning. Tell us about your office or admin background and the
              software you've used (even just Excel!). We'll take it from there.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                "No 'minimum years' cut-off — we match on attitude and fit",
                "Training is part of every role we place",
                "Confidential. We never forward your CV without your okay.",
                "Background check + ID verification handled at offer stage",
              ].map((l) => (
                <li key={l} className="flex items-start gap-3 text-navy/70">
                  <CheckCircle2 className="h-5 w-5 text-[#10B981] mt-0.5 flex-shrink-0" />
                  {l}
                </li>
              ))}
            </ul>

            <div className="mt-10 card-lux bg-[#F6FDF9] border-[#10B981]/20">
              <Smile className="h-5 w-5 text-[#10B981]" />
              <p className="mt-3 text-sm text-navy/75 leading-relaxed">
                <strong className="text-navy">New to payroll?</strong> Apply anyway. More than a third of the
                people we place started in a general office or admin role.
              </p>
            </div>
          </div>

          <div className="md:col-span-7">
            <form onSubmit={submitApplication} className="card-lux p-10 space-y-5" data-testid="cv-upload-form">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="cname">Full Name *</Label>
                  <Input id="cname" data-testid="cand-name" className="mt-2" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Alex Morgan" />
                </div>
                <div>
                  <Label htmlFor="cemail">Email *</Label>
                  <Input id="cemail" type="email" data-testid="cand-email" className="mt-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="alex@email.com" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="cphone">Phone</Label>
                  <Input id="cphone" data-testid="cand-phone" className="mt-2" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(646) 555-0112" />
                </div>
                <div>
                  <Label>Preferred State (for remote roles)</Label>
                  <Select value={form.preferred_state} onValueChange={(v) => setForm({ ...form, preferred_state: v })}>
                    <SelectTrigger className="mt-2" data-testid="cand-state"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Open to any">Open to any state</SelectItem>
                      <SelectItem value="Missouri">Missouri</SelectItem>
                      <SelectItem value="Oklahoma">Oklahoma</SelectItem>
                      <SelectItem value="Arizona">Arizona</SelectItem>
                      <SelectItem value="Kentucky">Kentucky</SelectItem>
                      <SelectItem value="Montana">Montana</SelectItem>
                      <SelectItem value="Arkansas">Arkansas</SelectItem>
                      <SelectItem value="Other">Other state</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="cbg">Tell us about your office or admin background</Label>
                <Textarea id="cbg" data-testid="cand-background" rows={4} className="mt-2" placeholder="Briefly describe your experience with office tasks or numbers. Receptionist? Data entry? Kept your team's spreadsheets tidy? All counts!" value={form.office_background} onChange={(e) => setForm({ ...form, office_background: e.target.value })} />
              </div>

              <div>
                <Label htmlFor="csoft">Are you familiar with any payroll tools? <span className="text-navy/50 font-normal">(It's okay if you're still learning!)</span></Label>
                <Input id="csoft" data-testid="cand-software" className="mt-2" placeholder="e.g. Excel, QuickBooks, Gusto, ADP Run — or 'just curious to learn!'" value={form.software_familiarity} onChange={(e) => setForm({ ...form, software_familiarity: e.target.value })} />
              </div>

              <div>
                <Label htmlFor="crole">What role are you hoping for?</Label>
                <Input id="crole" data-testid="cand-role" className="mt-2" placeholder="Payroll Assistant, Office Admin, Coordinator…" value={form.desired_role} onChange={(e) => setForm({ ...form, desired_role: e.target.value })} />
              </div>

              <div>
                <Label htmlFor="cnote">Anything else we should know?</Label>
                <Textarea id="cnote" data-testid="cand-note" rows={3} className="mt-2" placeholder="Relocation preferences, salary expectations, the kind of team culture you thrive in…" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </div>

              <div className="rounded-md border border-navy/10 bg-navy/[0.02] p-4 flex items-start gap-3" data-testid="cand-remote-check">
                <Checkbox id="remoteOk" checked={remoteOk} onCheckedChange={(v) => setRemoteOk(Boolean(v))} className="mt-0.5" />
                <label htmlFor="remoteOk" className="text-sm text-navy/75 cursor-pointer leading-relaxed">
                  <span className="font-medium text-navy">Open to remote roles.</span> Several of our employers
                  hire fully remote in MO, OK, AZ, KY, MT, and AR.
                </label>
              </div>

              <div>
                <Label>Upload your CV or a summary of your work history (PDF, DOC, DOCX — up to 8MB) *</Label>
                <label htmlFor="cv-input" className="mt-2 flex items-center justify-between gap-4 rounded-md border-2 border-dashed border-navy/15 bg-navy/[0.02] px-5 py-6 cursor-pointer hover:border-[#D4AF37] transition-colors" data-testid="cv-dropzone">
                  <div className="flex items-center gap-3">
                    <FileUp className="h-5 w-5 text-[#D4AF37]" />
                    <div>
                      <div className="text-sm font-medium text-navy">{cv ? cv.name : "Click to upload your CV"}</div>
                      <div className="text-xs text-navy/50">{cv ? `${(cv.size / 1024).toFixed(0)} KB` : "A simple summary is totally fine — don't stress about formatting."}</div>
                    </div>
                  </div>
                  <span className="eyebrow text-[#D4AF37]">Browse</span>
                </label>
                <input id="cv-input" type="file" accept=".pdf,.doc,.docx" onChange={handleCv} className="hidden" data-testid="cv-file-input" />
              </div>

              <div>
                <Label>Upload your ID for verification <span className="text-navy/50 font-normal">(optional — we'll ask at offer stage)</span></Label>
                <label htmlFor="id-input" className="mt-2 flex items-center justify-between gap-4 rounded-md border-2 border-dashed border-navy/15 bg-navy/[0.02] px-5 py-6 cursor-pointer hover:border-[#10B981] transition-colors" data-testid="id-dropzone">
                  <div className="flex items-center gap-3">
                    <IdCard className="h-5 w-5 text-[#10B981]" />
                    <div>
                      <div className="text-sm font-medium text-navy">{idDoc ? idDoc.name : "Driver's license, passport, or state ID"}</div>
                      <div className="text-xs text-navy/50">{idDoc ? `${(idDoc.size / 1024).toFixed(0)} KB` : "PDF, JPG, or PNG — encrypted at rest. Optional for now."}</div>
                    </div>
                  </div>
                  <span className="eyebrow text-[#10B981]">Browse</span>
                </label>
                <input id="id-input" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleId} className="hidden" data-testid="id-file-input" />
              </div>

              <div className="rounded-md border border-[#10B981]/25 bg-[#F6FDF9] p-4 flex items-start gap-3" data-testid="cand-bg-check">
                <Checkbox id="bgConsent" checked={bgConsent} onCheckedChange={(v) => setBgConsent(Boolean(v))} className="mt-0.5" />
                <label htmlFor="bgConsent" className="text-sm text-navy/80 cursor-pointer leading-relaxed">
                  <span className="font-medium text-navy">I consent to a standard background check.</span> Most
                  of our roles include a standard employment-law background check at the offer stage
                  (criminal record, identity, and employment history). You can withdraw consent any time
                  before offer.
                </label>
              </div>

              <button type="submit" disabled={submitting} className="btn-gold w-full disabled:opacity-70" data-testid="cv-submit-btn">
                {submitting ? "Submitting…" : "Submit My Application"}
                {!submitting && <ArrowRight className="h-4 w-4" />}
              </button>
              <p className="text-xs text-navy/45 text-center">We never forward your CV or ID without your consent.</p>
            </form>
          </div>
        </div>
      </section>

      {/* INTERVIEW PREP GUIDE LEAD MAGNET */}
      <section className="py-28 bg-navy text-white relative overflow-hidden" id="guide" data-testid="prep-guide-section">
        <div className="absolute inset-0 hero-grid opacity-20" />
        <div className="absolute -top-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#D4AF37]/10 blur-[120px]" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <div className="eyebrow text-[#D4AF37]">Free Download</div>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.05]">
            The Payroll Interview Prep Guide.
          </h2>
          <p className="mt-5 text-white/80 text-lg leading-relaxed max-w-2xl mx-auto">
            Plain-English tips for your first payroll interview — what to expect, what to say, and how to
            show your reliability and eye for detail.
          </p>

          <form onSubmit={handleGuide} className="mt-10 max-w-xl mx-auto grid md:grid-cols-[1fr_1fr_auto] gap-3" data-testid="guide-form">
            <Input placeholder="Full name" value={guideName} onChange={(e) => setGuideName(e.target.value)} data-testid="guide-name" className="bg-white text-navy border-white/20" />
            <Input type="email" placeholder="Email" value={guideEmail} onChange={(e) => setGuideEmail(e.target.value)} data-testid="guide-email" className="bg-white text-navy border-white/20" />
            <button disabled={guideLoading} className="btn-gold disabled:opacity-70" data-testid="guide-download-btn">
              {guideLoading ? "…" : "Get Guide"}
              {!guideLoading && <Download className="h-4 w-4" />}
            </button>
          </form>
          <p className="text-xs text-white/50 mt-4 flex items-center justify-center gap-2"><Mail className="h-3 w-3" /> Instant PDF. No follow-up spam.</p>
        </div>
      </section>
    </div>
  );
}
